// Preferências de apresentação. Não armazena linhas, permissões ou funções de renderização.
export const MAX_LAYOUTS = 20
export const emptyLibrary = () => ({ version: 1, active: 'system', custom: null, layouts: [] })
const unique = values => [...new Set(values)]
const keys = value => unique((Array.isArray(value) ? value : []).filter(key => typeof key === 'string' && key.length <= 200).slice(0, 1000))
export const cloneLayout = value => JSON.parse(JSON.stringify(value))
export const layoutName = value => String(value || '').trim().replace(/\s+/g, ' ').slice(0, 60)
export const searchText = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR')
export const TOTAL_OPERATIONS = { sum: 'Soma', min: 'Mínimo', max: 'Máximo', average: 'Média', count: 'Contagem', distinct: 'Contagem distinta' }
export function cleanTotals(value) {
  if (!Array.isArray(value)) return null
  const seen = new Set()
  return value.filter(item => item && typeof item.field === 'string' && item.field.length <= 200 && Object.prototype.hasOwnProperty.call(TOTAL_OPERATIONS, item.operation) && !seen.has(item.field + ':' + item.operation) && seen.add(item.field + ':' + item.operation)).slice(0, 30).map(item => ({ field: item.field, operation: item.operation }))
}

export function cleanSpec(raw = {}) {
  return {
    order: keys(raw?.order), hidden: keys(raw?.hidden),
    sort: { campo: typeof raw?.sort?.campo === 'string' ? raw.sort.campo.slice(0, 200) : '', direcao: raw?.sort?.direcao === 'desc' ? 'desc' : 'asc' }, totals: cleanTotals(raw?.totals)
  }
}

export function cleanLibrary(raw) {
  if (!raw || raw.version !== 1 || !Array.isArray(raw.layouts)) throw new Error('Formato de preferências de grid não reconhecido.')
  const ids = new Set()
  const layouts = raw.layouts.slice(0, MAX_LAYOUTS).filter(item => {
    if (!item || typeof item.id !== 'string' || !item.id || item.id.length > 100 || ['system', 'custom'].includes(item.id) || ids.has(item.id) || !layoutName(item.name)) return false
    ids.add(item.id); return true
  }).map(item => ({ id: item.id, name: layoutName(item.name), spec: cleanSpec(item.spec) }))
  const custom = raw.custom ? cleanSpec(raw.custom) : null
  const reference = value => typeof value === 'string' && value.length <= 100 ? value : null
  return { version: 1, active: ['system', ...(custom ? ['custom'] : []), ...layouts.map(item => item.id)].includes(raw.active) ? raw.active : 'system', custom,
    favorite: ['system', 'custom', ...layouts.map(item => item.id)].includes(raw.favorite) ? raw.favorite : null,
    sharedActive: reference(raw.sharedActive), sharedFavorite: reference(raw.sharedFavorite), layouts }
}

export function sourceColumns(columns) {
  const seen = new Set()
  return (columns || []).filter(col => {
    if (!col || col.visible === false || col.key == null || seen.has(String(col.key))) return false
    seen.add(String(col.key)); return true
  })
}
export function hardHidden(col) {
  const classes = `${col.class || ''} ${col.headerClass || ''} ${col.cellClass || ''}`
  return /(^|\s)d-none(\s|$)/.test(classes) && !/\bd-(sm|md|lg|xl|xxl)-table-cell\b/.test(classes)
}
const utilityColumn = col => ['acoes', 'actions', 'acao', 'action', 'expandir', 'selection'].includes(String(col.key))
export const isDataColumn = col => !!String(col.label || '').trim() && !hardHidden(col) && !utilityColumn(col)
export const lockedColumn = col => col.layoutLocked === true || !String(col.label || '').trim() || hardHidden(col) || utilityColumn(col)
export const configurableColumns = columns => sourceColumns(columns).filter(col => !hardHidden(col) && String(col.label || '').trim())

export function resolveLayout(columns, raw, initialSort) {
  const source = sourceColumns(columns), spec = cleanSpec(raw)
  const free = source.filter(col => !lockedColumn(col)), byKey = new Map(free.map(col => [String(col.key), col]))
  const ordered = unique([...spec.order.filter(key => byKey.has(key)), ...free.map(col => String(col.key))]).map(key => byKey.get(key))
  let index = 0
  const all = source.map(col => lockedColumn(col) ? col : ordered[index++])
  const hidden = new Set([...spec.hidden.filter(key => byKey.has(key)), ...free.filter(col => col.defaultHidden && (!raw || !spec.order.includes(String(col.key)))).map(col => String(col.key))])
  if (!all.some(col => isDataColumn(col) && !hidden.has(String(col.key)))) {
    const first = free.find(col => !hardHidden(col)); if (first) hidden.delete(String(first.key))
  }
  const visible = all.filter(col => lockedColumn(col) || !hidden.has(String(col.key)))
  const sortables = visible.filter(col => col.sortable !== false && !hardHidden(col))
  const fallback = initialSort || { campo: '', direcao: 'asc' }
  const preferred = raw ? spec.sort : fallback
  const column = sortables.find(col => String(col.key) === preferred.campo)
  const alternative = sortables.find(col => String(col.key) === fallback.campo) || sortables.find(col => col.label)
  const sort = !preferred.campo ? { campo: '', direcao: preferred.direcao } : column ? preferred : { campo: alternative ? String(alternative.key) : '', direcao: fallback.direcao === 'desc' ? 'desc' : 'asc' }
  return { all, visible, hidden, sort }
}

export function specForEditor(columns, raw, initialSort) {
  const result = resolveLayout(columns, raw, initialSort)
  return { order: result.all.map(col => String(col.key)), hidden: [...result.hidden], sort: result.sort, totals: cleanTotals(raw?.totals) }
}

export function renderedColumns(columns, raw, initialSort) {
  if (!raw) return sourceColumns(columns).filter(col => !col.defaultHidden || lockedColumn(col))
  const result = resolveLayout(columns, raw, initialSort)
  return result.visible.map(col => {
    if (lockedColumn(col)) return col
    // O layout pessoal escolhe as colunas visíveis também nas telas menores.
    const responsive = value => typeof value === 'string' ? value.replace(/\bd-none\b|\bd-(sm|md|lg|xl|xxl)-table-cell\b/g, '').trim() : value
    return { ...col, class: responsive(col.class), cellClass: responsive(col.cellClass), headerClass: responsive(col.headerClass) }
  })
}

export function activeSpec(library) {
  if (library.active === 'system') return null
  if (library.active === 'custom') return library.custom
  return library.layouts.find(item => item.id === library.active)?.spec || null
}

export function storageKey({ system, user, company, grid }) {
  if (user == null || user === '' || !grid) return null
  return 'escalador:grid-layouts:v1:' + [system, user, company ?? 'geral', grid].map(value => encodeURIComponent(String(value))).join(':')
}
export function readLibrary(storage, key) {
  if (!key) return { library: emptyLibrary(), raw: null, warning: 'Seu usuário não foi identificado. As alterações valerão somente nesta sessão.' }
  let raw = null
  try {
    raw = storage.getItem(key)
    if (raw == null) return { library: emptyLibrary(), raw, warning: '' }
    if (raw.length > 524288) throw new Error('Preferências muito extensas.')
    return { library: cleanLibrary(JSON.parse(raw)), raw, warning: '' }
  } catch { return { library: emptyLibrary(), raw, warning: 'Não foi possível ler os layouts deste navegador. O padrão do sistema está em uso.' } }
}
export function writeLibrary(storage, key, library, expectedRaw) {
  const clean = cleanLibrary(library)
  if (!key) return { library: clean, raw: null }
  if (storage.getItem(key) !== expectedRaw) throw new Error('O layout mudou em outra aba. Feche e abra esta configuração para carregar a versão atual.')
  const raw = JSON.stringify(clean)
  storage.setItem(key, raw)
  return { library: clean, raw }
}
